/**
 * AdminDashboardPage — Issue #366.
 *
 * Monthly administrative procurement / movement summary view:
 *   - total purchase amount + count (amount gated by admin_procurement:finance);
 *   - in / out / adjust counts + quantities;
 *   - ticket timely rate displayed if backend exposes a value, otherwise the
 *     UI shows the controlled empty-state reason — never fakes a percent.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_PROCUREMENT_PERMISSIONS,
  canSeeAdminProcurementFinance,
  hasPermission,
} from '@/lib/adminProcurementPermissions';
import { useAdminProcurementDashboard } from '@/hooks/useAdminProcurement';

function currentMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function fmtMoney(value: string | undefined | null, canSee: boolean): string {
  if (!canSee) return '受限';
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

export default function AdminDashboardPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_PROCUREMENT_PERMISSIONS.READ);
  const canSeeFinance = canSeeAdminProcurementFinance(permissions);

  const { summary, loading, error, fetchSummary } =
    useAdminProcurementDashboard();
  const [month, setMonth] = useState(currentMonth());

  useEffect(() => {
    if (!canRead) return;
    void fetchSummary(month);
  }, [canRead, month, fetchSummary]);

  if (!canRead) return <AccessDenied resource="行政看板" />;

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">行政看板</h1>
        <p className="text-sm text-slate-500">
          按月聚合行政采购金额、行政物品出入库件数与工单及时率（若 SLA 数据
          源已接入）。
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm text-slate-600">
          月份
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            data-testid="admin-dashboard-month"
          />
        </label>
      </div>

      {error ? (
        <Card
          className="border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="admin-dashboard-error"
        >
          {error.message}
        </Card>
      ) : null}

      {loading ? (
        <Card
          className="p-6 text-center text-sm text-slate-500"
          data-testid="admin-dashboard-loading"
        >
          加载中…
        </Card>
      ) : !summary ? (
        <Card
          className="p-6 text-center text-sm text-slate-500"
          data-testid="admin-dashboard-empty"
        >
          暂无数据
        </Card>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          data-testid="admin-dashboard-summary"
        >
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">本月行政采购金额</h2>
            <p className="text-2xl font-semibold" data-testid="dashboard-purchase-total">
              {fmtMoney(summary.totalPurchaseAmountDecimal, canSeeFinance)}
            </p>
            <p className="text-xs text-slate-500">
              共 {summary.totalPurchaseCount} 笔（已剔除作废）
            </p>
          </Card>
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">入库</h2>
            <p className="text-2xl font-semibold" data-testid="dashboard-in-qty">
              {summary.inQuantityDecimal}
            </p>
            <p className="text-xs text-slate-500">共 {summary.inCount} 笔</p>
          </Card>
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">出库</h2>
            <p className="text-2xl font-semibold" data-testid="dashboard-out-qty">
              {summary.outQuantityDecimal}
            </p>
            <p className="text-xs text-slate-500">共 {summary.outCount} 笔</p>
          </Card>
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">调整</h2>
            <p className="text-2xl font-semibold" data-testid="dashboard-adjust-count">
              {summary.adjustCount}
            </p>
            <p className="text-xs text-slate-500">笔次</p>
          </Card>
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">工单及时率</h2>
            {summary.ticketTimelyRateDecimal !== null ? (
              <p
                className="text-2xl font-semibold"
                data-testid="dashboard-timely-value"
              >
                {summary.ticketTimelyRateDecimal}
              </p>
            ) : (
              <p
                className="text-sm text-slate-500"
                data-testid="dashboard-timely-empty"
              >
                {summary.ticketTimelyEmptyReason ?? '尚未接入 SLA 数据'}
              </p>
            )}
          </Card>
          <Card className="space-y-1 p-4">
            <h2 className="text-xs uppercase text-slate-500">生成时间</h2>
            <p className="text-sm">{new Date(summary.generatedAt).toLocaleString('zh-CN')}</p>
          </Card>
        </div>
      )}
    </div>
  );
}
